import { communityApi } from '@/helpers/communityApi'

// The tab's active community at boot: the URL (?c=<id>) wins so every tab is
// independently pinned to the community in its address bar; localStorage is
// only the default for tabs opened without an explicit community (bookmarks,
// fresh windows). Written back by SET_CURRENT_COMMUNITY as "last used".
function resolveBootCommunityId() {
  const fromUrl = new URLSearchParams(window.location.search).get('c')
  if (fromUrl && /^\d+$/.test(fromUrl)) return Number(fromUrl)
  try {
    const stored = localStorage.getItem('currentCommunityId')
    return stored ? JSON.parse(stored) : null
  } catch (e) {
    return null
  }
}

export const state = {
  currentCommunityId: resolveBootCommunityId(),
  communities: [],
  currentSettings: null // Add this for onboarding needs
}

export const getters = {
  currentCommunityId: (state) => state.currentCommunityId,
  communities: (state) => state.communities,
  currentCommunity: (state) => state.communities.find(community => community.id === state.currentCommunityId) || null,
  currentSettings: (state) => state.currentSettings,
  // Add onboarding-specific getters
  hasTelegramBot: (state) => {
    return state.currentSettings && 
           state.currentSettings.telegram_bot_token && 
           state.currentSettings.telegram_bot_token.trim() !== ''
  }
}

export const mutations = {
  SET_CURRENT_COMMUNITY(state, communityId) {
    state.currentCommunityId = communityId;
    localStorage.setItem('currentCommunityId', JSON.stringify(communityId));

    // Auto-update currentSettings when community changes
    const community = state.communities.find(c => c.id === communityId);
    if (community && community.settings) {
      state.currentSettings = community.settings;
    }
  },

  SET_COMMUNITIES(state, communities) {
    state.communities = communities;
    localStorage.setItem('communities', JSON.stringify(communities));
  },

  SET_CURRENT_SETTINGS(state, settings) {
    state.currentSettings = settings;
  },

  UPDATE_CURRENT_SETTINGS(state, updates) {
    if (state.currentSettings) {
      state.currentSettings = { ...state.currentSettings, ...updates };
    }
  }
}

export const actions = {
  async createCommunity({ commit }, community) {
    const response = await communityApi.createCommunity(community);
    commit('SET_COMMUNITIES', response.data);
  },
  async getCommunity({ commit }, communityId) {
    const response = await communityApi.getCommunity(communityId);
    commit('SET_COMMUNITIES', response.data);
  },
  async updateCommunity({ commit }, community) {
    const response = await communityApi.updateCommunity(community);
    commit('SET_COMMUNITIES', response.data);
  },
  async deleteCommunity({ commit, state }, communityId) {
    await communityApi.deleteCommunity(communityId);
    const remaining = state.communities.filter(c => c.id !== communityId);
    commit('SET_COMMUNITIES', remaining);
    if (String(state.currentCommunityId) === String(communityId)) {
      if (remaining.length > 0) {
        commit('SET_CURRENT_COMMUNITY', remaining[0].id);
      } else {
        commit('SET_CURRENT_COMMUNITY', null);
        commit('SET_CURRENT_SETTINGS', null);
      }
    }
  },
  async getCommunities({ commit, state }) {
    const response = await communityApi.getCommunities();
    commit('SET_COMMUNITIES', response);
    let communityIds = response.map(community => community.id);
    if (response.length > 0 && (!state.currentCommunityId || !communityIds.includes(state.currentCommunityId))) {
      commit('SET_CURRENT_COMMUNITY', response[0].id);
    } else if (response.length === 0) {
      commit('SET_CURRENT_COMMUNITY', null);
      commit('SET_CURRENT_SETTINGS', null);
    }

    // Set current settings from the current community if available
    if (state.currentCommunityId) {
      const currentCommunity = response.find(c => c.id === state.currentCommunityId);
      if (currentCommunity && currentCommunity.settings) {
        commit('SET_CURRENT_SETTINGS', currentCommunity.settings);
      }
    }
  },
  
  // Add onboarding-specific actions
  async fetchCommunitySettings({ commit }, communityId) {
    try {
      const data = await communityApi.getCommunitySettingsOnly(communityId);
      commit('SET_CURRENT_SETTINGS', data);
      return data;
    } catch (error) {
      console.error('Failed to fetch community settings:', error);
      throw error;
    }
  },

  async updateCommunitySettings({ commit }, { id, community, settings }) {
    try {
      const data = await communityApi.updateCommunitySettings(id, { community, settings });
      commit('SET_CURRENT_SETTINGS', data.settings);
      return data;
    } catch (error) {
      console.error('Failed to update community settings:', error);
      throw error;
    }
  }
}

